import { ApplicationView, useController } from '@aha-app/mvc';
import getBoundingClientRect from '@popperjs/core/lib/dom-utils/getBoundingClientRect';
import getClippingRect from '@popperjs/core/lib/dom-utils/getClippingRect';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { get } from 'lodash';
import React, { useRef, useState } from 'react';
import 'tippy.js/dist/tippy.css';
import { type Instance as TippyInstance } from 'tippy.js/index';
import 'tippy.js/themes/light.css';
import { useTippyTarget } from '../components/tippyContext';
import WhiteboardController from '../controllers/WhiteboardController';
import styles from '../styles.module.css';
import type StylusCustomShape from '../stylus/StylusCustomShape';
import { type BaseTool } from '../tools/Tool';
import { type SingletonObject } from '../types';
import WithHint from './WithHint';
import { distanceBetweenPointsIsGreaterThan } from '../utils/geometry';

const DRAG_THRESHOLD = 8;

type Point = [number, number];

export interface ToolButtonProps<T extends typeof BaseTool> {
  className?: string;
  onMouseDown?: React.MouseEventHandler<HTMLElement>;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onDragStart?: (e: MouseEvent) => void;
  onDragEnd?: (e: MouseEvent) => void;
  onDrag?: (e: MouseEvent) => void;
  slot?: string;
  style?: any;
  popup?: JSX.Element;
  disabled?: boolean;
  draggable?: boolean;
  active?: boolean;
  tippyRef?: React.MutableRefObject<TippyInstance | undefined>;
  tippyIsEmojiPicker?: boolean;
  maxWidth?: number;
  id?: string | null;
  tool?: T | null;
  name?: string;
  testId?: string;
  children?: React.ReactNode;
  toolbarSingleton?: SingletonObject;
  onHoverMenu?: StylusCustomShape['onHoverMenu'];
}

const ToolButton = <T extends typeof BaseTool = typeof BaseTool>({
  className,
  children,
  onMouseDown,
  slot,
  style,
  onClick,
  onDragStart,
  onDrag,
  onDragEnd,
  popup,
  active,
  disabled,
  tippyRef,
  maxWidth,
  id = null,
  tool = null,
  name,
  testId,
  tippyIsEmojiPicker = false,
  draggable = false,
  onHoverMenu,
  ...props
}: ToolButtonProps<T>) => {
  const controller = useController(WhiteboardController);
  const buttonRef = useRef<HTMLDivElement>(null);
  const internalTippyRef = useRef<TippyInstance>();
  const [shouldRenderPopup, setShouldRenderPopup] = useState(false);

  const target = useTippyTarget();
  const toolbarSingleton = props.toolbarSingleton ?? target;

  // Mouse is dragging and we've hit the threshold
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPosition = useRef<Point>([0, 0]);
  const [mousePosition, setMousePosition] = useState([0, 0]);

  const moveStart = (e: React.MouseEvent<HTMLElement>) => {
    const moveUpdate = (e: MouseEvent) => {
      const { clientX: x, clientY: y } = e;
      setMousePosition([x, y]);
      setIsDragging(isDragging => {
        if (isDragging) {
          // Already passed threshold
          onDrag?.(e);
          return true;
        } else {
          // Check if we've dragged past the threshold
          const p = dragStartPosition.current;
          const passedThreshold = distanceBetweenPointsIsGreaterThan(
            { x, y },
            { x: p[0], y: p[1] },
            DRAG_THRESHOLD
          );
          if (passedThreshold) {
            onDragStart?.(e);
            return true;
          }
        }
        return isDragging;
      });
    };

    const moveEnd = (e: MouseEvent) => {
      setIsDragging(isDragging => {
        if (isDragging) {
          onDragEnd?.(e);
        }
        return false;
      });
      window.removeEventListener('mousemove', moveUpdate);
      window.removeEventListener('mouseup', moveEnd);
    };

    const { clientX: x, clientY: y } = e.nativeEvent;
    dragStartPosition.current = [x, y];
    setMousePosition([x, y]);

    // Attach to window so we can detect mouse position outside the window
    window.addEventListener('mousemove', moveUpdate);
    window.addEventListener('mouseup', moveEnd);
  };

  const mouseDownHandler = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }

    if (draggable) {
      moveStart(e);
    }

    // Prevent focus being taken, which messes with the text editor controls
    // where we want focus to stay in the editor while clicking buttons.
    e.preventDefault();

    controller.actionHideContextMenu();
    if (onMouseDown) {
      onMouseDown(e);
    }
  };

  const clickHandler = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }

    if (onClick) {
      onClick(e);
    }
  };

  let draggedButton: JSX.Element | undefined;
  if (isDragging && buttonRef.current) {
    let [left, top] = mousePosition;
    const parent = getPositionedAncestor(buttonRef.current);
    const parentRect = parent.getBoundingClientRect();
    const buttonRect = buttonRef.current.getBoundingClientRect();
    left -= parentRect.x + buttonRect.width / 2;
    top -= parentRect.y + buttonRect.height / 2;
    draggedButton = (
      <div
        className={classNames(
          styles.toolButton,
          className,
          styles.draggedButton
        )}
        style={{ left, top }}
      >
        {children}
      </div>
    );
  }

  const button = (
    <div
      className={classNames(styles.toolButton, className, {
        [styles.toolButtonActive]: active || shouldRenderPopup,
        [styles.toolButtonDisabled]: disabled,
        [styles.toolButtonHasHoverMenu]: !!onHoverMenu,
      })}
      onMouseDown={mouseDownHandler}
      onClick={clickHandler}
      slot={slot}
      style={style}
      data-testid={testId ?? tool?.testid()}
      role='button'
      ref={buttonRef}
      id={id ?? undefined}
    >
      {children}
      {onHoverMenu?.()}
    </div>
  );

  const withHint =
    toolbarSingleton && (tool || name) ? (
      <span slot={slot}>
        <WithHint tool={tool} name={name} singleton={toolbarSingleton}>
          {button}
        </WithHint>
      </span>
    ) : (
      button
    );

  if (popup) {
    return (
      <>
        <Tippy
          arrow={false}
          content={shouldRenderPopup ? popup : null}
          onShow={instance => {
            // When showing the tippy, get a reference to the instance so that
            // it can be controlled in a more fine-grained way - especially to
            // call `hide()` on demand to hide it.
            if (tippyRef) tippyRef.current = instance;
            internalTippyRef.current = instance;
            setShouldRenderPopup(true);
          }}
          onHide={() => setShouldRenderPopup(false)}
          theme='light'
          trigger='click'
          hideOnClick={true}
          plugins={[
            // prevents the tippy from flipping if its size changes such that it can use the default placement where
            // it previously couldn't, i.e. from color swatches (tall) to color picker (shorter)
            // copied from https://github.com/atomiks/tippyjs/issues/884#issuecomment-786626724
            {
              name: 'disableFlipAfterShow',
              defaultValue: true,

              fn({ props }) {
                const originalPlacement = get(props, 'placement');
                const originalPopperOptions = get(props, 'popperOptions');

                return {
                  onMount({ setProps, popperInstance }) {
                    setProps({
                      placement: popperInstance?.state.placement,
                      popperOptions: {
                        ...originalPopperOptions,
                        modifiers: [
                          ...(originalPopperOptions.modifiers || []),
                          {
                            name: 'flip',
                            enabled: false,
                          },
                        ],
                      },
                    });
                  },
                  onHidden({ setProps }) {
                    setProps({
                      placement: originalPlacement,
                      popperOptions: originalPopperOptions,
                    });
                  },
                };
              },
            },
          ]}
          popperOptions={{
            modifiers: [
              {
                name: 'applyAvailableHeight',
                enabled: true,
                phase: 'beforeWrite',
                requires: [],
                fn({ state }) {
                  const scrollable =
                    internalTippyRef.current?.popper.querySelector(
                      '.tippy-scrollable-element'
                    );
                  if (!scrollable) {
                    return;
                  }
                  // logic extracted and adapted from popper's detectOverflow modifier
                  const refRect = getBoundingClientRect(
                    state.elements.reference
                  );
                  const clipRect = getClippingRect(
                    state.elements.popper,
                    'clippingParents',
                    'viewport',
                    'absolute'
                  );

                  const spaceUp = refRect.top - clipRect.top;
                  const spaceDown = clipRect.bottom - refRect.bottom;
                  const space = Math.max(spaceUp, spaceDown) - 20; // hardcode some padding

                  (scrollable as HTMLElement).style.maxHeight = `${space}px`;
                },
              },
              {
                name: 'placeToSideIfHeightConstrained',
                enabled: true,
                phase: 'afterWrite',
                requires: [],
                fn({ state }) {
                  if (!tippyIsEmojiPicker) {
                    return;
                  }
                  const refRect = getBoundingClientRect(
                    state.elements.reference
                  );
                  const clipRect = getClippingRect(
                    state.elements.popper,
                    'clippingParents',
                    'viewport',
                    'absolute'
                  );

                  const spaceUp = refRect.top - clipRect.top;
                  const spaceDown = clipRect.bottom - refRect.bottom;
                  const emojiPickerRequiredHeight = 450;
                  if (
                    spaceUp < emojiPickerRequiredHeight &&
                    spaceDown < emojiPickerRequiredHeight
                  ) {
                    const spaceRight = clipRect.right - refRect.right;
                    state.placement = spaceRight > 350 ? 'right' : 'left';
                  }
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  altBoundary: true,
                },
              },
            ],
          }}
          onClickOutside={(_, e) => {
            // don't close if a context menu was clicked, as that might be triggered
            // from inside the tippy popup
            const ahaMenuClicked = (e.target as HTMLElement).closest(
              'aha-menu-content'
            );
            if (!ahaMenuClicked) {
              internalTippyRef.current?.hide();
            }
          }}
          interactive
          duration={75}
          animation='scale'
          maxWidth={maxWidth}
        >
          {withHint}
        </Tippy>
        {draggedButton}
      </>
    );
  }

  return (
    <>
      {withHint}
      {draggedButton}
    </>
  );
};

/**
 * Find the parent element that 'position: absolute' will be relative to
 */
function getPositionedAncestor(el: HTMLElement): HTMLElement {
  let parent = el.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    if (['relative', 'absolute', 'fixed', 'sticky'].includes(style.position)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.body;
}

export default ApplicationView(ToolButton);
